import { FC, useEffect, useState } from "react";

import Stack from "@mui/material/Stack";
import { categoryNameLabelKeyForLocale, hasMultipleLocales, Locales } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { DeleteCategoryConfirmModal } from "./DeleteCategoryConfirmModal";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import {
  localeToCategoryFieldKey,
  RateCategory,
  useRateCategories,
} from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useRateCategories";
import { Add, Delete, Lists } from "@/icons/outlined";

interface EditCategoryModalProps {
  open: boolean;
  category: RateCategory | null;
  onClose: () => void;
  onDeleted?: (deletedId: string) => void;
}

interface EditCategoryFormData {
  primary: string;
  translation: string;
}

const getNameForLocale = (category: RateCategory | null, locale: Locales | null): string => {
  if (!locale) {
    return "";
  }
  return category?.fields.find((f) => f.locale === locale)?.name ?? "";
};

export const EditCategoryModal: FC<EditCategoryModalProps> = ({ open, category, onClose, onDeleted }) => {
  const { t } = useTranslate();
  const { renameCategory, deleteCategory, isRenaming, isDeleting } = useRateCategories();
  const { apiFormObject, formHasTranslation, secondaryLocale } = useStableLiveFormEditorContext<"ticketing">();
  const primaryLocale = apiFormObject.locale;

  const initialPrimary = getNameForLocale(category, primaryLocale);
  const initialTranslation = getNameForLocale(category, secondaryLocale);
  const initiallyHadTranslation = initialTranslation.length > 0;
  const defaultShowTranslation = formHasTranslation;

  const [showTranslation, setShowTranslation] = useState(defaultShowTranslation);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const form = useForm<EditCategoryFormData>({
    defaultValues: { primary: initialPrimary, translation: initialTranslation },
    shouldUnregister: true,
  });
  const { control, formState, handleSubmit, reset } = form;

  useEffect(() => {
    if (open && category) {
      reset({ primary: initialPrimary, translation: initialTranslation });
      setShowTranslation(defaultShowTranslation);
    }
  }, [open, category, reset, initialPrimary, initialTranslation, defaultShowTranslation]);

  const onSubmit = handleSubmit(async (values) => {
    if (!category) {
      return;
    }
    const trimmedPrimary = values.primary.trim();
    const trimmedTranslation = (values.translation ?? "").trim();

    const primaryKey = localeToCategoryFieldKey(primaryLocale);
    const secondaryKey = secondaryLocale ? localeToCategoryFieldKey(secondaryLocale) : null;

    const fields: { en?: string; fr?: string; es?: string; de?: string } = {};
    if (trimmedPrimary && trimmedPrimary !== initialPrimary) {
      fields[primaryKey] = trimmedPrimary;
    }
    if (secondaryKey) {
      if (showTranslation && trimmedTranslation && trimmedTranslation !== initialTranslation) {
        fields[secondaryKey] = trimmedTranslation;
      } else if (!showTranslation && initiallyHadTranslation) {
        fields[secondaryKey] = "";
      }
    }

    if (fields.en === undefined && fields.fr === undefined && fields.es === undefined && fields.de === undefined) {
      onClose();
      return;
    }

    const { category: renamed, errorMessage } = await renameCategory({ rateCategoryId: category.id, fields });
    if (!renamed) {
      enqueueSnackbar(errorMessage || t("dashboard", "ticketing.categories.rename.error"), { vibe: "danger" });
      return;
    }
    onClose();
  });

  const handleConfirmDelete = async () => {
    if (!category) {
      return;
    }
    const { deletedId, errorMessage } = await deleteCategory(category.id);
    if (!deletedId) {
      enqueueSnackbar(errorMessage || t("dashboard", "ticketing.categories.delete.error"), { vibe: "danger" });
      return;
    }
    onDeleted?.(deletedId);
    setIsConfirmDeleteOpen(false);
    onClose();
  };

  const primaryLabel =
    showTranslation && secondaryLocale
      ? t("dashboard", categoryNameLabelKeyForLocale(primaryLocale))
      : t("dashboard", "ticketing.categories.modal.name");
  const translationLabel = secondaryLocale ? t("dashboard", categoryNameLabelKeyForLocale(secondaryLocale)) : "";

  const organizationHasMultipleLocales = hasMultipleLocales(apiFormObject.organization?.country);

  return (
    <>
      <DashboardDialog open={open && !isConfirmDeleteOpen} onClose={onClose} maxWidth="sm">
        <DashboardDialogBody
          slots={{
            header: (
              <DashboardDialogHeader icon={<Lists />} title={t("dashboard", "ticketing.categories.edit.title")} />
            ),
            content: (
              <DashboardDialogContent>
                <Stack spacing={1.5}>
                  <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                    {t("dashboard", "ticketing.categories.edit.description")}
                  </DashboardTypography>
                  <form noValidate onSubmit={onSubmit}>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="flex-start">
                        <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }} spacing={0.5}>
                          <DashboardTextField
                            size="medium"
                            control={control}
                            name="primary"
                            label={primaryLabel}
                            placeholder={t("dashboard", "ticketing.categories.modal.placeholder")}
                            errorMessage={formState.errors.primary?.message}
                            rules={[isRequired(t("dashboard", "ticketing.categories.modal.required"))]}
                          />
                        </Stack>
                        {organizationHasMultipleLocales &&
                          !showTranslation &&
                          (formHasTranslation ? (
                            <DashboardButton
                              variant="text"
                              vibe="neutral"
                              startIcon={<Add />}
                              onClick={() => setShowTranslation(true)}
                              sx={{ alignSelf: "flex-end" }}
                            >
                              {t("dashboard", "ticketing.categories.modal.addTranslation")}
                            </DashboardButton>
                          ) : (
                            <DashboardTooltip
                              title={t("dashboard", "ticketing.categories.modal.addTranslationDisabledTooltip")}
                            >
                              <span style={{ alignSelf: "flex-end", display: "inline-flex" }}>
                                <DashboardButton variant="text" vibe="neutral" startIcon={<Add />} disabled>
                                  {t("dashboard", "ticketing.categories.modal.addTranslation")}
                                </DashboardButton>
                              </span>
                            </DashboardTooltip>
                          ))}
                        {showTranslation && secondaryLocale && (
                          <>
                            <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }} spacing={0.5}>
                              <DashboardTextField
                                size="medium"
                                control={control}
                                name="translation"
                                label={translationLabel}
                                placeholder={t("dashboard", "ticketing.categories.modal.placeholder")}
                                errorMessage={formState.errors.translation?.message}
                              />
                            </Stack>
                            <DashboardIconButton
                              size="medium"
                              vibe="neutral"
                              aria-label={t("dashboard", "ticketing.categories.edit.removeTranslation")}
                              onClick={() => setShowTranslation(false)}
                              sx={{ alignSelf: "flex-end" }}
                            >
                              <Delete />
                            </DashboardIconButton>
                          </>
                        )}
                      </Stack>
                      <Stack direction="row">
                        <DashboardButton variant="outlined" vibe="danger" onClick={() => setIsConfirmDeleteOpen(true)}>
                          {t("dashboard", "ticketing.categories.edit.delete")}
                        </DashboardButton>
                      </Stack>
                    </Stack>
                  </form>
                </Stack>
              </DashboardDialogContent>
            ),
            footer: (
              <DashboardDialogFooter>
                <DashboardButton onClick={onSubmit} isLoading={isRenaming}>
                  {t("dashboard", "ticketing.categories.modal.save")}
                </DashboardButton>
                <DashboardButton vibe="neutral" variant="outlined" onClick={onClose}>
                  {t("dashboard", "common.cancel")}
                </DashboardButton>
              </DashboardDialogFooter>
            ),
          }}
        />
      </DashboardDialog>
      <DeleteCategoryConfirmModal
        open={isConfirmDeleteOpen}
        categoryName={initialPrimary || initialTranslation}
        isLoading={isDeleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setIsConfirmDeleteOpen(false)}
      />
    </>
  );
};
